// Public storefront building blocks, shared by the chat StoreView and the
// public site-builder pages (/g/:orgSlug and custom domains). The product UI
// lives in ./StoreParts and imports neither the app store nor the auth provider,
// so a storefront page never pulls the app in; the chat mini-shop screen
// (chat/StoreView) is the one piece that needs the app store and stays there.
export {
  ProductDetail,
  ProductCard,
  MobileFullScreenProductCard,
  CartPill,
  useCartPill,
  formatPrice,
  stripHtml,
  isDigital,
  shouldShow,
} from './StoreParts';

export {
  fetchAllProducts,
  addToCart,
  getCart,
  openCheckout,
  optimizeImageUrl,
  setStorefrontConfig,
  isStorefrontConfigured,
  type StoreProduct,
  type Cart,
  type StorefrontConfig,
} from '@/services/shopifyStorefront';

export { loadStorefrontConfig } from './storeConfig';

import { addToCart, type StoreProduct } from '@/services/shopifyStorefront';
import { shouldShow } from './StoreParts';
import { fireCartBeacon } from './storeConfig';

/** Distinct Shopify collections present across the sellable products, for
 *  building collection pickers in the site builder. Deduped by handle. */
export function collectionsOf(products: StoreProduct[]): { handle: string; title: string }[] {
  const map = new Map<string, string>();
  products.filter(shouldShow).forEach((p) => p.collections.forEach((c) => map.set(c.handle, c.title)));
  return [...map.entries()].map(([handle, title]) => ({ handle, title }));
}

/** True when a product belongs to the collection with the given handle. */
export function inCollection(p: StoreProduct, handle: string): boolean {
  return p.collections.some((c) => c.handle === handle);
}

/** Resolve a product by its stable Shopify ID first, then by handle as a
 *  fallback (legacy refs / recovery). Handles are owned by Shopify and can
 *  change, so IDs are the canonical key; when a match is found by ID, the
 *  returned product's `handle` is the current LIVE handle. Returns undefined
 *  when neither matches (e.g. product deleted/unpublished on Shopify). */
export function findProduct(
  products: StoreProduct[],
  ref: { id?: string | null; handle?: string | null },
): StoreProduct | undefined {
  if (ref.id) {
    const byId = products.find((p) => p.id === ref.id);
    if (byId) return byId;
  }
  if (ref.handle) return products.find((p) => p.handle === ref.handle);
  return undefined;
}

/** A product's representative price (its lowest variant price) for sorting. */
export function productPrice(p: StoreProduct): number {
  const amounts = p.variants.map((v) => parseFloat(v.price?.amount || '')).filter((n) => !Number.isNaN(n));
  return amounts.length ? Math.min(...amounts) : 0;
}

/** Default product ordering for every dropdown and grid: products that have a
 *  builder page (have been "opened") come first, then price high → low. Pass a
 *  set identifying products that have a page — entries may be product IDs OR
 *  handles (each product is matched by either), so it works during the id
 *  migration; omit it to sort by price alone. */
export function sortProducts(products: StoreProduct[], withPage?: ReadonlySet<string>): StoreProduct[] {
  const hasPage = (p: StoreProduct) => !!withPage && (withPage.has(p.id) || withPage.has(p.handle));
  return [...products].sort((a, b) => {
    const ap = hasPage(a) ? 1 : 0;
    const bp = hasPage(b) ? 1 : 0;
    if (ap !== bp) return bp - ap;
    return productPrice(b) - productPrice(a);
  });
}

// Add a variant to the cart and broadcast the same `cart-updated` event the
// chat StoreView dispatches, so the shared <CartPill> opens/updates everywhere.
// This is the only add-to-cart path site pages should use.
export async function addToCartAndNotify(variantId: string, quantity = 1): Promise<void> {
  const updated = await addToCart(variantId, quantity);
  fireCartBeacon();
  window.dispatchEvent(new CustomEvent('cart-updated', { detail: updated }));
}
